import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useRelationshipImport } from '../../hooks/use-relationship-import'
import type { RelationshipImportExecuteResult } from '../../types/relationship-import'
import {
  RelationshipFileDropZone,
  RelationshipValidationResults,
  RelationshipSuccessDisplay,
  RelationshipErrorDisplay,
  LoadingSpinner,
  type FileDropZoneProps,
} from './RelationshipImportModal.parts'

interface RelationshipImportModalProps {
  caseId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: ((result: RelationshipImportExecuteResult) => void) | undefined
}

interface ModalContentProps {
  caseId: string
  phase: string
  loading: boolean
  error: string | null
  validationResult: ReturnType<typeof useRelationshipImport>['validationResult']
  importResult: RelationshipImportExecuteResult | null
  selectedFileName: string
  dropZoneProps: FileDropZoneProps
  onExecute: () => void | Promise<void>
  onClose: () => void
  onRetry: () => void
}

function ModalContent({
  caseId,
  phase,
  loading,
  error,
  validationResult,
  importResult,
  selectedFileName,
  dropZoneProps,
  onExecute,
  onClose,
  onRetry,
}: ModalContentProps): React.ReactElement {
  const { t } = useTranslation('cases')
  if (phase === 'idle') return <RelationshipFileDropZone {...dropZoneProps} />
  if (phase === 'validating')
    return (
      <LoadingSpinner
        message={t('infrastructure.importRelationship.validating', { fileName: selectedFileName })}
      />
    )
  if (phase === 'validated' && validationResult)
    return (
      <RelationshipValidationResults
        result={validationResult}
        caseId={caseId}
        onExecute={onExecute}
        onCancel={onClose}
        loading={loading}
      />
    )
  if (phase === 'executing')
    return <LoadingSpinner message={t('infrastructure.importRelationship.importingRecords')} />
  if (phase === 'complete' && importResult)
    return <RelationshipSuccessDisplay result={importResult} onClose={onClose} />
  if (phase === 'error' && error)
    return <RelationshipErrorDisplay error={error} onRetry={onRetry} onClose={onClose} />
  return <div />
}

interface ImportHandlers {
  handleClose: () => void
  handleRetry: () => void
  handleExecute: () => Promise<void>
  handleDrag: (event: React.DragEvent) => void
  handleDrop: (event: React.DragEvent) => void
  handleBrowse: () => void
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  dragActive: boolean
  selectedFile: File | null
}

function useImportHandlers({
  caseId,
  onClose,
  onSuccess,
}: Pick<RelationshipImportModalProps, 'caseId' | 'onClose' | 'onSuccess'>): ImportHandlers & {
  phase: string
  loading: boolean
  error: string | null
  validationResult: ReturnType<typeof useRelationshipImport>['validationResult']
  importResult: RelationshipImportExecuteResult | null
  fileInputRef: React.RefObject<HTMLInputElement>
} {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { phase, loading, error, validationResult, importResult, validate, execute, reset } =
    useRelationshipImport(caseId)

  const handleClose = useCallback(() => {
    reset()
    setSelectedFile(null)
    onClose()
  }, [reset, onClose])

  const handleRetry = useCallback(() => {
    reset()
    setSelectedFile(null)
  }, [reset])

  const handleFileSelect = useCallback(
    async (file: File) => {
      setSelectedFile(file)
      await validate(file)
    },
    [validate]
  )

  const handleExecute = useCallback(async () => {
    const result = await execute()
    if (result) onSuccess?.(result)
  }, [execute, onSuccess])

  const handleBrowse = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleDrag = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDragActive(event.type === 'dragenter' || event.type === 'dragover')
    },
    []
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDragActive(false)
      const file = event.dataTransfer.files[0]
      if (file?.name.endsWith('.csv')) void handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) void handleFileSelect(file)
    },
    [handleFileSelect]
  )

  return {
    handleClose, handleRetry, handleExecute,
    handleDrag, handleDrop, handleBrowse, handleInputChange,
    dragActive, selectedFile,
    phase, loading, error, validationResult, importResult, fileInputRef,
  }
}

export function RelationshipImportModal({
  caseId,
  isOpen,
  onClose,
  onSuccess,
}: RelationshipImportModalProps): React.ReactElement | null {
  const { t } = useTranslation('cases')
  const handlers = useImportHandlers({ caseId, onClose, onSuccess })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('infrastructure.importRelationship.title')}
          </h2>
          <button
            onClick={handlers.handleClose}
            aria-label={t('actions.cancel')}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <ModalContent
            caseId={caseId}
            phase={handlers.phase}
            loading={handlers.loading}
            error={handlers.error}
            validationResult={handlers.validationResult}
            importResult={handlers.importResult}
            selectedFileName={handlers.selectedFile?.name ?? ''}
            dropZoneProps={{
              caseId,
              dragActive: handlers.dragActive,
              onDrag: handlers.handleDrag,
              onDrop: handlers.handleDrop,
              onBrowse: handlers.handleBrowse,
              fileInputRef: handlers.fileInputRef,
              onInputChange: handlers.handleInputChange,
            }}
            onExecute={handlers.handleExecute}
            onClose={handlers.handleClose}
            onRetry={handlers.handleRetry}
          />
        </div>
      </div>
    </div>
  )
}
